import {
  ArcGisMapServerImageryProvider,
  ImageryLayer,
  ImageryProvider,
  Rectangle,
  UrlTemplateImageryProvider,
  WebMapTileServiceImageryProvider,
  WebMercatorTilingScheme,
} from 'cesium';
import { config } from '../app/config';

// The cache's published extent (service fullExtent, converted to WGS84).
// Requests outside it return HTTP 400 — not 404 — so without this clamp Cesium
// probes the whole visible globe and spams failed requests over NSW/ocean.
const QLD_IMAGERY_RECTANGLE = Rectangle.fromDegrees(137.742, -29.51, 153.759, -9.002);

/**
 * Queensland "Latest State Program" aerial imagery, draped on the globe/terrain.
 *
 * Primary path: the cached WMTS endpoint (standards-compliant, hits the tile
 * cache directly). Fallback: the ArcGIS ImageServer via Cesium's native
 * provider.
 *
 * The `url` is the RESTful tile-URL *template* (with {TileMatrix}/{TileRow}/
 * {TileCol} placeholders), NOT the GetCapabilities document — Cesium's
 * WebMapTileServiceImageryProvider does not parse capabilities, so pointing it
 * at the .xml made every "tile" request return the capabilities XML (HTTP 200)
 * which can't be decoded as an image. The layer/style/tileMatrixSet identifiers
 * are taken verbatim from GetCapabilities (S1).
 */
export async function createQldImageryProvider(): Promise<ImageryProvider> {
  try {
    return new WebMapTileServiceImageryProvider({
      url: config.imagery.wmtsTileTemplate,
      layer: config.imagery.layer,
      style: config.imagery.style,
      tileMatrixSetID: config.imagery.tileMatrixSetID,
      format: config.imagery.format,
      // The Qld cache is published in Web Mercator (EPSG:3857); aligns with
      // Cesium's WGS84 globe to sub-metre (GDA↔WGS84 offset ~1.8 m — see S3).
      tilingScheme: new WebMercatorTilingScheme(),
      maximumLevel: 20,
      rectangle: QLD_IMAGERY_RECTANGLE,
    });
  } catch (err) {
    console.warn('[imagery] WMTS provider failed, falling back to ImageServer', err);
    return ArcGisMapServerImageryProvider.fromUrl(config.imagery.imageServer);
  }
}

export async function addQldImagery(addTo: {
  imageryLayers: { addImageryProvider(p: ImageryProvider): ImageryLayer };
}): Promise<void> {
  // OSM is added first as an always-available base so the globe is never plain
  // blue when the QLD tile service is unavailable. QLD aerial renders on top
  // where its tiles load successfully.
  addTo.imageryLayers.addImageryProvider(
    new UrlTemplateImageryProvider({
      url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      credit: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maximumLevel: 19,
    }),
  );
  try {
    const provider = await createQldImageryProvider();
    addTo.imageryLayers.addImageryProvider(provider);
  } catch (err) {
    console.warn('[imagery] QLD imagery unavailable, OSM fallback will remain', err);
  }
}
